/* eslint-disable no-underscore-dangle */
/**
 * "Assign" / "Change" for one trip, in the portal's BottomSheet — ONE sheet,
 * used by both portals, told apart only by the `source` it is handed:
 *
 *   operatorAssignSource    (OperatorPortalApi) the operator's own active
 *                           drivers and cars, on its own trips. No operator
 *                           to choose — it is always itself — and no money.
 *   dispatcherAssignSource  (DispatcherPortalApi) every active operator, and
 *                           that operator's drivers and cars, on any trip.
 *                           The office also sets THE OPERATOR'S FEE here.
 *
 * A source is `{ office, load(trip), save(input), copy }`; `load` returns
 * `{ operators?, drivers, vehicles, slot }` already reduced to
 * `{ id, label, operatorId? }` choices, so this file knows nothing about
 * either endpoint's shape. The operator portal behaves exactly as it always
 * has: an empty driver or vehicle keeps the one that is there.
 *
 * THE FEE IS THE OFFICE'S ALONE. An `office` source shows what the operator
 * on this slot is paid — the figure the dispatcher is about to replace — and
 * sends the new one with the assignment. An operator source has no fee field
 * at all.
 *
 * `add` OPENS THE SAME SHEET ON A SLOT THAT DOES NOT EXIST YET — a guide
 * beside the driver. The only differences are that it asks for the new
 * slot's ROLE (which a change never asks for: a slot's role is what it is)
 * and that it starts empty rather than on whoever is there now. Everything
 * else is the sheet you already know: the operator narrows the driver and
 * vehicle lists, the fee is required, one Save.
 *
 * Saving notifies the customer exactly as the emailed assign link always
 * has — the sheet says so, so nobody is surprised.
 *
 * EVERY CHOICE IS TYPED AT, NOT SCROLLED THROUGH (Components/ChoicePicker):
 * one combobox, used for all three lists. It changes how a row is reached
 * and NOTHING about which rows there are — the operator still narrows the
 * driver and vehicle lists, changing it still drops crew that is no longer
 * that operator's, and the operator portal still sees the two fields it
 * always did.
 */
import React, {
  useEffect, useId, useMemo, useState,
} from 'react';
import BottomSheet from '../Components/BottomSheet';
import ChoicePicker from '../Components/ChoicePicker';
import { formatTime } from '../../../Utils/timezone';
import { CREW_ROLES, OPERATOR_ROLE_LABELS } from '../../../config/bookingConstants';
import {
  driverText, operatorErrorMessage, operatorLoadErrorMessage, routeLabel, shortDay, vehicleText,
} from './operatorFormat';

/** The currencies an operator fee is agreed in (the API takes these two). */
const FEE_CURRENCIES = ['EUR', 'HUF'];

/**
 * What a new slot can be, in the order the office thinks of them: the crew
 * roles, the same four the API's portal schema takes. Never Cost — a cost is
 * filed from a receipt or the dashboard, not dispatched. A slot's role is
 * never guessed, so an addition always says which.
 */
const ROLES = CREW_ROLES.map((id) => ({ id, label: OPERATOR_ROLE_LABELS[id].label }));

const EMPTY = {
  operators: [], drivers: [], vehicles: [], slot: {},
};

export default function AssignSheet({
  trip, source, onClose, onAssigned, add = false,
}) {
  const operatorFieldId = useId();
  const driverFieldId = useId();
  const vehicleFieldId = useId();
  const feeFieldId = useId();
  const feeCurrencyId = useId();
  const roleFieldId = useId();

  const [choices, setChoices] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // An ADDITION starts on nobody: the people on the trip now are the OTHER
  // slot's, and pre-filling them would offer to put the driver on twice.
  const [operatorId, setOperatorId] = useState(add ? '' : trip?.operator?.id || '');
  const [driverId, setDriverId] = useState(add ? '' : trip?.driver?.id || '');
  const [carId, setCarId] = useState(add ? '' : trip?.vehicle?.id || '');
  const [feeAmount, setFeeAmount] = useState('');
  const [feeCurrency, setFeeCurrency] = useState('EUR');
  // A new slot is a guide far more often than anything else — that is the
  // case the office cannot do from the board today.
  const [role, setRole] = useState('guide');

  const { office, copy } = source;

  useEffect(() => {
    let cancelled = false;
    source.load(trip, { add })
      .then((loaded) => {
        if (cancelled) return;
        setChoices({ ...EMPTY, ...loaded });
        // The slot as the server has it. Only the operator and the fee are
        // put into the form: an empty driver or vehicle means "leave it",
        // which is the contract both endpoints have always had.
        const slot = loaded.slot || {};
        if (slot.operatorId) setOperatorId(slot.operatorId);
        // A board row names a plate, never a car id, so the vehicle the
        // office is replacing can only come from here.
        if (slot.driverId !== undefined) setDriverId(slot.driverId);
        if (slot.carId !== undefined) setCarId(slot.carId);
        const amount = slot.fee?.amount;
        if (amount !== undefined && amount !== null) setFeeAmount(String(amount));
        if (slot.fee?.currency) setFeeCurrency(slot.fee.currency);
      })
      .catch((failure) => {
        if (!cancelled) setError(operatorLoadErrorMessage(failure));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
    // One read per trip: the sheet is opened on one row and closed again.
  }, [trip?._id]);

  // An addition is always a new operator on a new slot, whoever it is: there
  // is nobody on it to keep, so the fee has to be said.
  const operatorChanged = office && (add || operatorId !== (trip?.operator?.id || ''));

  // A driver or a car belongs to exactly one operator, and the API refuses
  // any other pairing — so the lists narrow to the operator on the sheet.
  // An operator source tags nothing, and then every row stays.
  const mine = (rows) => rows.filter((row) => !row.operatorId || row.operatorId === operatorId);
  const drivers = useMemo(() => mine(choices.drivers), [choices.drivers, operatorId]);
  const vehicles = useMemo(() => mine(choices.vehicles), [choices.vehicles, operatorId]);

  // Changing the operator drops a driver and car that are no longer its own,
  // so the sheet never offers a pairing the API would refuse.
  const chooseOperator = (next) => {
    setOperatorId(next);
    if (!choices.drivers.some((row) => row.id === driverId && row.operatorId === next)) setDriverId('');
    if (!choices.vehicles.some((row) => row.id === carId && row.operatorId === next)) setCarId('');
  };

  // A new operator brings its own people: whoever is on the trip now works
  // for the operator that is leaving, so "keep" is not on offer. Nor is it
  // on an addition, which has nobody on it to keep.
  const keeps = !operatorChanged;

  /**
   * What an empty driver or vehicle list SAYS.
   *
   * The lists narrow to the operator on the sheet, so before one is chosen
   * they are empty for a reason the reader can act on — and "this operator
   * has no drivers" would be about an operator that is not there. That is
   * the ordinary state of an ADDITION and of a trip nobody has taken on;
   * it was simply never said out loud before.
   */
  const officeEmpty = (what) => {
    if (!office) {
      return `You have no active ${what} yet. Add them under Drivers & vehicles.`;
    }

    return operatorId
      ? `This operator has no ${what} to choose from.`
      : `Choose an operator first, and its ${what} appear here.`;
  };

  const save = async (event) => {
    event.preventDefault();

    if (office && !operatorId) {
      setError('Choose an operator.');
      return;
    }

    if (!office && !driverId && !carId) {
      setError('Choose a driver or a vehicle.');
      return;
    }

    // A trip is never handed to a new operator for an unsaid amount, and a
    // new slot is agreed like any other — the API refuses both, and the
    // sheet says so before the round trip.
    if (operatorChanged && feeAmount === '') {
      setError('Set what we pay this operator for this trip.');
      return;
    }

    setSaving(true);
    setError('');

    const fee = office && feeAmount !== ''
      ? { amount: Number(feeAmount), currency: feeCurrency }
      : undefined;

    try {
      const updated = await source.save({
        trip,
        operatorId: office ? operatorId : undefined,
        driverId: driverId || undefined,
        carId: carId || undefined,
        fee: fee === undefined ? undefined : fee,
        add,
        role: add ? role : undefined,
      });
      onAssigned(updated);
    } catch (failure) {
      setError(operatorErrorMessage(failure, { 404: copy.notFound }));
      setSaving(false);
    }
  };

  const nothingToChoose = !loading && !choices.drivers.length && !choices.vehicles.length
    && !(choices.operators || []).length;

  // What is on the slot now — what the office is replacing.
  const nowOnSlot = [
    trip?.operator?.name || 'No operator',
    driverText(trip) || 'No driver',
    vehicleText(trip),
  ].filter(Boolean).join(' · ');

  const subtitle = [
    trip?.booking_reference,
    `${shortDay(trip?.date_datetime)} ${formatTime(trip?.date_datetime)}`.trim(),
    routeLabel(trip),
  ].filter(Boolean).join(' · ');

  return (
    <BottomSheet
      title={add ? copy.addTitle : copy.title(trip)}
      subtitle={subtitle}
      onClose={onClose}
      closeDisabled={saving}
      closeLabel="Close"
    >
      <form className="pp-op-form" onSubmit={save} noValidate>
        {loading ? <div className="pp-state" role="status">{copy.loading}</div> : null}

        {nothingToChoose ? <p className="pp-op-form__hint">{copy.empty}</p> : null}

        {/* What is on the slot now — what the office is replacing. An
            addition replaces nothing, and says what it is doing instead. */}
        {office ? (
          <p className="pp-op-form__hint">
            {add ? `${nowOnSlot} — this adds a second one, and leaves them as they are.` : nowOnSlot}
          </p>
        ) : null}

        {/* A new slot names its own role; an existing slot's role is what it
            is, and is never asked for. */}
        {add && !loading ? (
          <>
            <label className="pp-op-form__label" htmlFor={roleFieldId}>Role</label>
            <select
              id={roleFieldId}
              className="pp-op-form__select"
              value={role}
              onChange={(event) => setRole(event.target.value)}
              disabled={saving}
            >
              {ROLES.map((one) => <option key={one.id} value={one.id}>{one.label}</option>)}
            </select>
          </>
        ) : null}

        {/*
          ONE picker, three times — operator, driver, vehicle. Typing narrows
          the list it is given; WHICH list each one is given is still decided
          here and nowhere else (`mine`, above), so the sheet can no more
          offer a foreign driver than it ever could.

          Not while the choices are still on the wire, and not when there are
          none at all: a picker with nothing in it says so, and three fields
          each saying it under `copy.empty` would be the same news four times.
        */}
        {loading || nothingToChoose ? null : (
          <>
            {office ? (
              <ChoicePicker
                id={operatorFieldId}
                label="Operator"
                value={operatorId}
                onChange={chooseOperator}
                disabled={saving}
                placeholder="Type to find an operator"
                choices={choices.operators || []}
                clearLabel="Clear the operator"
                empty="There are no active operators to choose from."
                noMatch="No operator matches what you typed."
              />
            ) : null}

            <ChoicePicker
              id={driverFieldId}
              label="Driver"
              value={driverId}
              onChange={setDriverId}
              disabled={saving}
              placeholder={trip?.driver && keeps ? 'Keep the current driver' : 'Type to find a driver'}
              choices={drivers}
              clearLabel="Clear the driver"
              empty={officeEmpty('drivers')}
              noMatch="No driver matches what you typed."
            />

            <ChoicePicker
              id={vehicleFieldId}
              label="Vehicle"
              value={carId}
              onChange={setCarId}
              disabled={saving}
              placeholder={trip?.vehicle && keeps ? 'Keep the current vehicle' : 'Type to find a vehicle'}
              choices={vehicles}
              clearLabel="Clear the vehicle"
              empty={officeEmpty('vehicles')}
              noMatch="No vehicle matches what you typed."
            />
          </>
        )}

        {/* Only on a CHANGE: an addition takes nobody off. */}
        {!add && operatorChanged && trip?.driver ? (
          <p className="pp-op-form__hint">
            {`${driverText(trip)} works for ${trip?.operator?.name || 'the previous operator'} and comes off this trip.`}
          </p>
        ) : null}

        {office ? (
          <>
            <label className="pp-op-form__label" htmlFor={feeFieldId}>Operator fee</label>
            <div className="pp-op-form__money">
              <input
                id={feeFieldId}
                className="pp-op-form__input"
                type="number"
                min="0"
                step="any"
                inputMode="decimal"
                value={feeAmount}
                onChange={(event) => setFeeAmount(event.target.value)}
                disabled={loading || saving}
                placeholder="0"
              />
              <label className="pp-op-form__sr-only" htmlFor={feeCurrencyId}>Fee currency</label>
              <select
                id={feeCurrencyId}
                className="pp-op-form__select pp-op-form__select--unit"
                value={feeCurrency}
                onChange={(event) => setFeeCurrency(event.target.value)}
                disabled={loading || saving}
              >
                {FEE_CURRENCIES.map((code) => <option key={code} value={code}>{code}</option>)}
              </select>
            </div>
            <p className="pp-op-form__hint">
              What Pickup Hungary pays this operator for this trip.
              The customer&apos;s price is not shown here.
            </p>
          </>
        ) : null}

        <p className="pp-op-form__hint">{copy.hint}</p>

        {error ? <div className="pp-op-error" role="alert">{error}</div> : null}

        <button
          type="submit"
          className="pp-btn pp-btn--primary pp-btn--block"
          disabled={loading || saving}
        >
          {saving ? 'Saving…' : 'Save and notify'}
        </button>
      </form>
    </BottomSheet>
  );
}
